import type { ReactNode } from "react";
import { type AssistantCard, AssistantTier } from "@/generated/backend/v1/assistant";
import { Struct } from "@/generated/google/protobuf/struct";
import type {
  AssumptionsCard,
  CashFlow,
  DebtCard,
  PlanOverview,
  PriorityStatus,
  Projection,
  RetirementOutlook,
  ShortfallCard,
  TaxYearCard,
  UpcomingActions,
} from "@/plan-logic/assistant/cards";
import { formatDate, formatMonth, fromIso, isSet } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import { Markdown } from "./markdown";

/**
 * Cards: tool results drawn as what they are. A calculated card is the engine's plan (or the
 * household's data) read by plan-logic, exactly the object the model was given; a reviewed card is
 * an FAQ entry word for word. They're styled apart from the model's words (assistant.css).
 */

const day = (iso?: string) => (iso ? formatDate(fromIso(iso)) : "");
const month = (iso?: string) => (iso ? formatMonth(fromIso(iso)) : "");

/** Handles as people read them. */
export function who(handle: string | undefined): string {
  if (!handle || handle === "self") return "You";
  if (handle === "partner") return "Your partner";
  const m = /^(member|child)_(\d+)$/.exec(handle);
  return m ? `${m[1] === "child" ? "Child" : "Member"} ${m[2]}` : handle;
}

function Rows({ rows }: { rows: [ReactNode, ReactNode, string?][] }) {
  return (
    <dl className="ac-rows">
      {rows
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v, tone], i) => (
          <div key={i} className={tone}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
    </dl>
  );
}

const pct = (v?: number) => (v === undefined ? "" : `${Math.round(v)}%`);

function Overview({ d }: { d: PlanOverview }) {
  return (
    <Rows
      rows={[
        ["Net worth today", d.netWorthToday !== undefined ? money(d.netWorthToday) : ""],
        [
          "Cash",
          d.shortfall
            ? `Runs short in ${month(d.shortfall.month)}, by ${money(d.shortfall.amountShort)}`
            : "Doesn't run short",
          d.shortfall ? "bad" : "good",
        ],
        ["Retirement", d.retirement ? `${month(d.retirement.date)}, at ${d.retirement.age}` : ""],
        [
          "Nest egg then",
          d.retirement
            ? `${money(d.retirement.nestEggProjected)} of ${money(d.retirement.nestEggRequired)} needed`
            : "",
        ],
        ["Priorities on track", `${d.priorities.onTrack} of ${d.priorities.total}`],
        ["Debt-free", d.debtFree ? month(d.debtFree) : ""],
      ]}
    />
  );
}

function Upcoming({ d }: { d: UpcomingActions }) {
  if (!d.actions.length) return <p className="ac-empty">Nothing to do in these months.</p>;
  return (
    <ol className="ac-list">
      {d.actions.map((a, i) => (
        <li key={i}>
          <span className="ac-when">{day(a.date)}</span>
          <span>
            {a.what}
            {a.times > 1 && (
              <em>
                {" "}
                · {a.times} times, {money(a.total)} in all
              </em>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

function Balances({ d }: { d: Projection }) {
  const side = (s: string) => d.balances.filter((b) => b.side === s);
  return (
    <>
      <Rows
        rows={[
          ["Net worth", money(d.netWorth)],
          ["You own", money(d.own)],
          ["You owe", money(d.owe)],
        ]}
      />
      {["account", "asset", "debt"].map((s) =>
        side(s).length ? (
          <Rows
            key={s}
            rows={side(s).map((b) => [
              b.name || b.kind,
              money(b.balance),
              s === "debt" ? "owe" : undefined,
            ])}
          />
        ) : null,
      )}
    </>
  );
}

function Flow({ d }: { d: CashFlow }) {
  return (
    <>
      <p className="ac-note">
        A month, averaged over {d.months} months from {month(d.from)}.
      </p>
      <Rows
        rows={[
          ["Income before tax", money(d.incomeBeforeTax)],
          ["Tax and deductions", money(d.deductions)],
          ["Take-home", money(d.takeHome)],
          ...d.spending.map((s) => [s.category, money(s.monthly)] as [string, string]),
          ["Left after the bills", money(d.leftOverAfterBills), "strong"],
        ]}
      />
    </>
  );
}

function Priorities({ d }: { d: PriorityStatus }) {
  return (
    <ul className="ac-list">
      {d.priorities.map((p, i) => (
        <li key={i}>
          <span className={`ac-pill ${p.onTrack ? "good" : "warn"}`}>{pct(p.fundedPercent)}</span>
          <span>
            {p.name}
            {p.firstDue && <em> · due {month(p.firstDue)}</em>}
            {p.withoutAbiPercent !== undefined && (
              <em> · {pct(p.withoutAbiPercent)} on your current path</em>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Retirement({ d }: { d: RetirementOutlook }) {
  return (
    <>
      {d.members.map((m, i) => (
        <Rows
          key={i}
          rows={[
            [who(m.handle), `${month(m.date)}, at ${m.age}`, "strong"],
            ["Spending a year (today's dollars)", money(m.targetAnnualSpending)],
            ["Nest egg needed", money(m.nestEggRequired)],
            [
              "Nest egg projected",
              money(m.nestEggProjected),
              m.nestEggProjected >= m.nestEggRequired ? "good" : "warn",
            ],
          ]}
        />
      ))}
    </>
  );
}

function Shortfall({ d }: { d: ShortfallCard }) {
  if (!d.runsShort) return <p className="ac-empty good">Your plan doesn't run short of cash.</p>;
  return (
    <>
      <Rows
        rows={[
          ["Runs short", month(d.month), "bad"],
          ["Short that month", d.amountShort !== undefined ? money(d.amountShort) : ""],
          ["Deepest", d.deepest !== undefined ? money(d.deepest) : ""],
        ]}
      />
      {d.fixes.length > 0 && (
        <ul className="ac-list">
          {d.fixes.map((f, i) => (
            <li key={i}>
              <span className={`ac-pill ${f.closesIt ? "good" : "warn"}`}>
                {f.closesIt ? "Fixes it" : "Helps"}
              </span>
              <span>{f.what}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Debts({ d }: { d: DebtCard }) {
  return (
    <>
      <ul className="ac-list">
        {d.debts.map((x, i) => (
          <li key={i}>
            <span className="ac-when">{money(x.balance)}</span>
            <span>
              {x.name || x.kind}
              <em>
                {" "}
                · {money(x.monthlyPayment)} a month
                {x.paidOff ? `, paid off ${month(x.paidOff)}` : ""}
              </em>
            </span>
          </li>
        ))}
      </ul>
      <Rows
        rows={[
          ["Debt-free", d.debtFree ? month(d.debtFree) : "Not within the plan"],
          ["Interest over the plan", money(d.lifetimeInterest)],
        ]}
      />
    </>
  );
}

function Tax({ d }: { d: TaxYearCard }) {
  return (
    <>
      {d.members.map((m, i) => (
        <Rows
          key={i}
          rows={[
            [who(m.handle), "", "strong"],
            ["Income", money(m.totalIncome)],
            ["Taxable income", money(m.taxableIncome)],
            ["Federal tax", money(m.federalTax)],
            ["Provincial tax", money(m.provincialTax)],
            ["CPP and EI", money(m.cpp + m.ei)],
            ["RRSP deduction", m.rrspDeduction ? money(m.rrspDeduction) : ""],
            [
              m.balanceOwing < 0 ? "Refund" : "Balance owing",
              money(Math.abs(m.balanceOwing)),
              m.balanceOwing < 0 ? "good" : undefined,
            ],
          ]}
        />
      ))}
    </>
  );
}

function Assumptions({ d }: { d: AssumptionsCard }) {
  const p = (v?: number) => (v === undefined ? "" : `${+v.toFixed(2)}%`);
  return (
    <Rows
      rows={[
        ["Inflation", p(d.inflationPercent)],
        ["Salary growth", p(d.salaryGrowthPercent)],
        ["Investment return", p(d.investmentReturnPercent)],
        ["Retirement confidence", p(d.retirementConfidencePercent)],
        ["Life expectancy", d.lifeExpectancyAge ? `age ${d.lifeExpectancyAge}` : ""],
      ]}
    />
  );
}

function Fallback({ d }: { d: Record<string, unknown> }) {
  return (
    <Rows
      rows={Object.entries(d)
        .filter(([, v]) => typeof v !== "object")
        .map(([k, v]) => [k, String(v)])}
    />
  );
}

const BODIES: Record<string, (d: never) => ReactNode> = {
  get_plan_overview: (d: PlanOverview) => <Overview d={d} />,
  get_upcoming_actions: (d: UpcomingActions) => <Upcoming d={d} />,
  get_balances: (d: Projection) => <Balances d={d} />,
  get_cash_flow: (d: CashFlow) => <Flow d={d} />,
  get_priorities: (d: PriorityStatus) => <Priorities d={d} />,
  get_retirement: (d: RetirementOutlook) => <Retirement d={d} />,
  get_shortfall: (d: ShortfallCard) => <Shortfall d={d} />,
  get_debts: (d: DebtCard) => <Debts d={d} />,
  get_tax_year: (d: TaxYearCard) => <Tax d={d} />,
  get_assumptions: (d: AssumptionsCard) => <Assumptions d={d} />,
};

type Faq = { question: string; answer: string; written: string };

export function CardView({ card }: { card: AssistantCard }) {
  const data = (card.data ? Struct.toJson(card.data) : {}) as Record<string, unknown>;
  if (card.tier === AssistantTier.REVIEWED) {
    const faq = data as unknown as Faq;
    return (
      <section className="ac-card reviewed" data-testid="assistant-card-reviewed">
        <header>
          <span className="ac-badge">From the Wealthy Planet FAQ</span>
          <h4>{card.title}</h4>
        </header>
        <div className="ac-body ac-prose">
          <Markdown text={faq.answer ?? ""} policy="app-and-web" />
        </div>
        {faq.written && <footer>General information, written {month(faq.written)}.</footer>}
      </section>
    );
  }
  const body = BODIES[card.kind];
  const stale = data.planStale === true;
  return (
    <section className="ac-card calc" data-testid={`assistant-card-${card.kind}`}>
      <header>
        <span className="ac-badge">Calculated by Abi&rsquo;s planning engine</span>
        <h4>{card.title}</h4>
      </header>
      <div className="ac-body">{body ? body(data as never) : <Fallback d={data} />}</div>
      <footer>
        {isSet(card.asOf) ? `From your plan of ${formatDate(card.asOf)}` : "From your plan"}
        {stale && ". Your latest changes aren’t in it yet; a new plan is being made."}
      </footer>
    </section>
  );
}
